import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";

import "./AllProjects.css";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { getUserProjects } from "../../services/userService";
import {
  getProjectAudienceActorsPath,
  getProjectAudiencesPath,
  getProjectPath,
  getProjectStrategiesPath,
  getProjectStrategyBlocksPath,
} from "../../utils/collectionPaths";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { useUsersPermissions } from "../../contexts/UsersPermissionsContext";
import { useProjectModal } from "../../contexts/ProjectModalContext";
import CloseIcon from "@mui/icons-material/Close";

async function deleteProjectWithSubcollections(projectId) {
  // First, get all project members and clean up their userProjects collections
  const membersRef = collection(db, `projects/${projectId}/members`);
  const membersSnapshot = await getDocs(membersRef);

  // Remove project from all members' userProjects collections
  const cleanupPromises = membersSnapshot.docs.map((memberDoc) => {
    const userId = memberDoc.id;
    const userProjectRef = doc(db, `users/${userId}/userProjects/${projectId}`);
    return deleteDoc(userProjectRef).catch((error) => {
      console.warn(
        `Failed to remove project ${projectId} from user ${userId}'s userProjects:`,
        error
      );
      // Continue with other cleanup even if one fails
    });
  });

  // Wait for all userProjects cleanup to complete
  await Promise.all(cleanupPromises);

  // Top-level subcollections
  const subcollectionsToDelete = [
    "tasks",
    "audiences",
    "strategies",
    "members",
    "invitedUsers",
  ];

  for (const sub of subcollectionsToDelete) {
    if (sub === "audiences") {
      const audienceColRef = collection(db, getProjectAudiencesPath(projectId));
      const audienceDocs = await getDocs(audienceColRef);
      for (const audDoc of audienceDocs.docs) {
        // Delete actors subcollection for each audience
        const actorsColRef = collection(
          db,
          getProjectAudienceActorsPath(projectId, audDoc.id)
        );
        const actorDocs = await getDocs(actorsColRef);
        for (const actDoc of actorDocs.docs) {
          // Delete actor subcollections (fears, contexts, etc.)
          const subcollections = [
            "coreJobs",
            "contexts",
            "fears",
            "bigJobs",
            "subtasks",
            "competitors",
            "habits",
          ];
          for (const subcollectionName of subcollections) {
            const subcollectionRef = collection(
              db,
              `projects/${projectId}/audiences/${audDoc.id}/actors/${actDoc.id}/${subcollectionName}`
            );
            const subcollectionSnapshot = await getDocs(subcollectionRef);
            // Delete all documents in the subcollection
            for (const subDoc of subcollectionSnapshot.docs) {
              await deleteDoc(subDoc.ref);
            }
          }
          // Then delete the actor document itself
          await deleteDoc(actDoc.ref);
        }
        // Then delete the audience document itself
        await deleteDoc(audDoc.ref);
      }
    } else if (sub === "strategies") {
      const strategiesColRef = collection(
        db,
        getProjectStrategiesPath(projectId)
      );
      const strategyDocs = await getDocs(strategiesColRef);
      for (const stratDoc of strategyDocs.docs) {
        // Delete 'blocks' subcollection for each strategy document
        const blocksColRef = collection(
          db,
          getProjectStrategyBlocksPath(projectId, stratDoc.id)
        );
        const blockDocs = await getDocs(blocksColRef);
        for (const blockDoc of blockDocs.docs) {
          // Delete 'screens' subcollection for each block
          const screensColRef = collection(
            db,
            `projects/${projectId}/strategies/${stratDoc.id}/blocks/${blockDoc.id}/screens`
          );
          const screensDocs = await getDocs(screensColRef);
          for (const screenDoc of screensDocs.docs) {
            await deleteDoc(screenDoc.ref);
          }
          // Delete the block document itself
          await deleteDoc(blockDoc.ref);
        }
        // Then delete the strategy document itself
        await deleteDoc(stratDoc.ref);
      }
    } else {
      // Handle other top-level subcollections like 'tasks', 'members', 'invitedUsers'
      const subColRef = collection(db, `projects/${projectId}/${sub}`);
      const subDocs = await getDocs(subColRef);
      for (const d of subDocs.docs) {
        await deleteDoc(d.ref);
      }
    }
  }
  // Por fim, deleta o documento do projeto
  await deleteDoc(doc(db, getProjectPath(projectId)));
}

function AllProjects() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");
  const [_successMessage, setSuccessMessage] = useState("");

  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuProjectId, setMenuProjectId] = useState(null);

  const [sortBy, _setSortBy] = useState("creationDate");
  const [sortOrder, _setSortOrder] = useState("desc");

  const { openCreateProjectModal } = useProjectModal();
  const { openUsersPermissionsDialog } = useUsersPermissions();

  const fetchProjects = useCallback(async () => {
    if (!currentUser) {
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setPageError("");
    try {
      // Get projects from the user's userProjects collection
      const userProjects = await getUserProjects(currentUser.uid);

      const projectList = userProjects.map((userProject) => ({
        ...userProject.project,
        // The membership document id is authoritative. Some older project
        // payloads also carry an empty `id` field, so assign this after spread.
        id: userProject.id,
        role: userProject.role,
        // Ensure creationDate is a JS Date object for formatting
        creationDate: userProject.project.creationDate?.toDate
          ? userProject.project.creationDate.toDate()
          : userProject.project.creationDate || null,
      }));

      setProjects(projectList);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setPageError("Failed to load projects. Please try refreshing.");
    }
    setLoading(false);
  }, [currentUser]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const sortedProjects = React.useMemo(() => {
    const sorted = [...projects];
    sorted.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      if (sortBy === "creationDate") {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      } else {
        aValue = aValue || "";
        bValue = bValue || "";
      }
      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [projects, sortBy, sortOrder]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );

  const handleMenuOpen = (event, projectId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuProjectId(projectId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuProjectId(null);
  };

  const handleOpenEditModal = (project) => {
    setEditingProject({ ...project });
    setFormError("");
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditingProject(null);
    setFormError("");
  };

  const handleUpdateProject = async () => {
    if (
      !editingProject ||
      !editingProject.projectName?.trim() ||
      !currentUser
    ) {
      setFormError("Project name cannot be empty for update.");
      return;
    }
    setFormError("");
    try {
      const projectRef = doc(db, getProjectPath(editingProject.id));
      await updateDoc(projectRef, {
        projectName: editingProject.projectName.trim(),
        description: editingProject.description || "",
      });
      setSuccessMessage(
        `Project "${editingProject.projectName}" updated successfully!`
      );
      fetchProjects();
      handleCloseEditModal();
    } catch (err) {
      console.error("Error updating project:", err);
      setFormError("Failed to update project. Please try again.");
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (
      !currentUser ||
      !window.confirm(
        "Are you sure you want to delete this project and all its data? This includes all associated tasks, audiences, actors, and strategies."
      )
    )
      return;
    setPageError("");
    try {
      setLoading(true);
      // Use the comprehensive delete function (includes cleanup of all members' userProjects)
      await deleteProjectWithSubcollections(projectId);
      setSuccessMessage(
        "Project and all associated data deleted successfully!"
      );
      // Refresh projects list
      fetchProjects(); // fetchProjects will filter out the deleted project implicitly by re-fetching
    } catch (err) {
      console.error("Error deleting project and its subcollections:", err);
      setPageError("Failed to delete project and its data. Please try again.");
    }
  };

  const handleOpenUsersPermissions = (project) => {
    openUsersPermissionsDialog(project);
  };

  return (
    <Box
      sx={{
        position: "relative",
        height:'100svh',
        width: '100vw',
        overflow: 'hidden',
        p: { xs: 0, md: '25px' },
      }}
    >

      {/* Projects card section */}
      <Box sx={{  }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
            }}
          >
            <Typography
              variant="h4"
            >
              All Projects
            </Typography>

            <Button
              size="small"
              variant="outlined"
              onClick={openCreateProjectModal}
              sx={{ gap: '4px', alignItems: 'center', lineHeight: 1 }}
            >
              <AddIcon sx={{ width: '16px', height: '16px' }} />
              Add New Project
            </Button>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: "24px",
              backgroundColor: "transparent",
            }}
          >
            {pageError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setPageError("")}
              >
                {pageError}
              </Alert>
            )}

            {/* Projects */}
            {sortedProjects.map((project) => (
              <Card
                key={project.id}
                data-testid={`project-card-${project.id}`}
                sx={{
                  height: "175px",
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={(event) => {
                  if (menuAnchorEl && menuProjectId === project.id) {
                    event.stopPropagation();
                    return;
                  }
                  navigate(`/project/${project.id}`);
                }}
              >
                <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        handleMenuOpen(e, project.id);
                      }}
                      aria-label="actions"
                      aria-controls={
                        menuProjectId === project.id
                          ? `project-menu-${project.id}`
                          : undefined
                      }
                      aria-haspopup="true"
                    >
                      <MoreVertIcon />
                    </IconButton>

                    <Menu
                      id={`project-menu-${project.id}`}
                      anchorEl={menuAnchorEl}
                      open={
                        Boolean(menuAnchorEl) && menuProjectId === project.id
                      }
                      onClose={handleMenuClose}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      PaperProps={{ sx: { minWidth: 160 } }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleOpenEditModal(project);
                          handleMenuClose();
                        }}
                      >
                        Edit
                      </MenuItem>

                      {project.role === "owner" && (
                        <MenuItem
                          onClick={() => {
                            handleOpenUsersPermissions(project);
                            handleMenuClose();
                          }}
                        >
                          Users & Permissions
                        </MenuItem>
                      )}
                    </Menu>
                  </Box>

                  <Box>
                    <Typography gutterBottom variant="h5" component="div">
                      {project.projectName}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {project.description}
                    </Typography>
                  </Box>

                  <Box sx={{ position: "absolute", bottom: 8, right: 8 }}>
                    <Tooltip title="Delete project">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteProject(project.id);
                          handleMenuClose();
                        }}
                      >
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Edit Project Dialog */}
      {editingProject && (
        <Dialog
          open={openEditModal}
          onClose={handleCloseEditModal}
          fullWidth
          maxWidth="sm"
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
                backdropFilter: 'blur(20px)',
              }
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px',
              padding: '0 0 8px',
            }}
          >
            Edit Project: {editingProject?.projectName}
            <IconButton
              onClick={handleCloseEditModal}
              sx={{
                backgroundColor: (theme) => theme.palette.action.hover,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              padding: '0',
              my: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {formError && (
              <Typography color="error" sx={{ mb: 1, fontSize: "0.9rem" }}>
                {formError}
              </Typography>
            )}
            <TextField
              autoFocus
              margin="dense"
              id="edit-project-name"
              label="Project Name"
              type="text"
              fullWidth
              variant="outlined"
              value={editingProject?.projectName || ""}
              onChange={(e) =>
                setEditingProject({
                  ...editingProject,
                  projectName: e.target.value,
                })
              }
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              id="edit-project-description"
              label="Project Description"
              type="text"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={editingProject?.description || ""}
              onChange={(e) =>
                setEditingProject({
                  ...editingProject,
                  description: e.target.value,
                })
              }
            />
          </DialogContent>
          <DialogActions sx={{ p: "16px 0 0" }}>
            <Button onClick={handleUpdateProject} variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

export default AllProjects;
